import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const DataTable = ({
  title,
  columns,
  data,
  rowsPerPageOptions = [10, 25, 50, 100, 500, "All"],
  searchable = true,
}) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  const highlightText = (value, search) => {
    if (!search || value === null || value === undefined) return value;

    const text = value.toString();
    const regex = new RegExp(`(${search})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-warning text-dark">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Filter and sort data
  const filteredData = useMemo(() => {
    let filtered = data;

    if (search) {
      filtered = data.filter((item) =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(search.toLowerCase())
      );
    }

    if (sortField) {
      filtered.sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        if (typeof aVal === "string" && !isNaN(Date.parse(aVal))) {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        } else if (typeof aVal === "string" && aVal.includes("$")) {
          aVal = parseFloat(aVal.replace(/[^0-9.-]+/g, ""));
          bVal = parseFloat(bVal.replace(/[^0-9.-]+/g, ""));
        } else {
          aVal = aVal?.toString().toLowerCase() || "";
          bVal = bVal?.toString().toLowerCase() || "";
        }

        if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [search, sortField, sortOrder, data]);

  const totalPages =
    rowsPerPage === "All" ? 1 : Math.ceil(filteredData.length / rowsPerPage);

  const currentData =
    rowsPerPage === "All"
      ? filteredData
      : filteredData.slice(
          (currentPage - 1) * rowsPerPage,
          currentPage * rowsPerPage
        );

  const renderSortIcon = (field) => {
    if (sortField !== field) return "↕";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredData.map((row) => {
      const obj = {};
      columns.forEach((col) => {
        obj[col.label] = row[col.field];
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${title}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = columns.map((col) => col.label);
    const tableRows = filteredData.map((row) =>
      columns.map((col) => row[col.field])
    );

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 160, 133] },
    });

    doc.save(`${title}.pdf`);
  };

  return (
    <div className="card basic-data-table">
      {/* Header */}
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 className="card-title mb-1">{title}</h5>
          <div className="text-muted align-items-right">
            <span className="badge text-lg bg-primary me-1">Total: {data.length}</span>
            <span className="badge text-lg bg-success me-1">
              Filtered: {filteredData.length}
            </span>
            <span className="badge text-lg bg-danger">Showing: {currentData.length}</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card-body">
        <div className="dt-layout-row mb-3 d-flex flex-wrap align-items-center gap-2">
          <div className="dt-length me-2">
            <select
              className="form-select w-auto"
              value={rowsPerPage}
              onChange={(e) => {
                const val =
                  e.target.value === "All"
                    ? "All"
                    : parseInt(e.target.value, 10);
                setRowsPerPage(val);
                setCurrentPage(1);
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <label> entries per page</label>
          </div>
          <div className="align-items-center">
            <button
                className="btn btn-success btn-sm me-2"
                onClick={exportToExcel}
            >
                Export Excel
            </button>
            { /*<button className="btn btn-danger btn-sm" onClick={exportToPDF}>
                Download PDF
            </button>*/ }
          </div>

          {searchable && (
            <div className="dt-search ms-auto">
              <label htmlFor="dt-search-0" className="me-2">
                Search:
              </label>
              <input
                type="text"
                className="form-control w-auto dt-input"
                id="dt-search-0"
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}

        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table bordered-table mb-0 dataTable">
            <thead className="table-light">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.field}
                    onClick={() => col.sortable && handleSort(col.field)}
                    style={{ cursor: col.sortable ? "pointer" : "default" }}
                  >
                    {col.label} {col.sortable ? renderSortIcon(col.field) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentData.length > 0 ? (
                currentData.map((row, i) => (
                  <tr key={i}>
                    {columns.map((col) => (
                      <td key={col.field}>
                        {col.render
                          ? col.render(row)
                          : highlightText(row[col.field], search)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {rowsPerPage !== "All" && totalPages > 1 && (
          <nav className="mt-3">
            <ul className="pagination justify-content-end mb-0">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </button>
              </li>

              {(() => {
                const pages = [];
                const maxVisible = 5;
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, currentPage + 2);

                if (startPage > 1) {
                  pages.push(
                    <li key={1} className="page-item">
                      <button className="page-link" onClick={() => setCurrentPage(1)}>
                        1
                      </button>
                    </li>
                  );
                  if (startPage > 2) {
                    pages.push(
                      <li key="start-ellipsis" className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                }

                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <li
                      key={i}
                      className={`page-item ${currentPage === i ? "active" : ""}`}
                    >
                      <button className="page-link" onClick={() => setCurrentPage(i)}>
                        {i}
                      </button>
                    </li>
                  );
                }

                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <li key="end-ellipsis" className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  pages.push(
                    <li key={totalPages} className="page-item">
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        {totalPages}
                      </button>
                    </li>
                  );
                }

                return pages;
              })()}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
};

export default DataTable;
